import { supabase } from '../lib/supabase';
import { clientAnalyticsService } from './clientAnalyticsService';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const emailTemplateService = {
  async getAllTemplates(filters = {}) {
    try {
      let query = supabase
        ?.from('email_templates')
        ?.select(`
          *,
          creator:user_profiles!email_templates_created_by_fkey(
            id,
            full_name,
            email
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.segment) {
        query = query?.eq('segment', filters?.segment);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching templates:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      throw error;
    }
  },

  // Preset feedback-specific email templates
  getFeedbackTemplatePresets() {
    return [
      {
        id: 'preset-low-rating',
        templateName: 'Low Satisfaction Rating Follow-up',
        segment: 'feedback_low_rating',
        subjectLine: 'Queremos mejorar su experiencia - Folio {{folio_ot}}',
        templateBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb;">
  <div style="background-color: #ffffff; border-radius: 8px; padding: 30px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
    <h2 style="color: #dc2626; margin-bottom: 20px;">Estimado/a {{client_name}},</h2>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Hemos recibido su calificación de <strong style="color: #dc2626;">{{satisfaction_rating}} estrellas</strong> para el servicio del folio <strong>{{folio_ot}}</strong>.
    </p>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Lamentamos que su experiencia no haya cumplido con sus expectativas. Su opinión es muy importante para nosotros y queremos hacer las cosas bien.
    </p>
    
    <div style="background-color: #fef2f2; border-left: 4px solid #dc2626; padding: 15px; margin: 20px 0; border-radius: 4px;">
      <p style="color: #991b1b; margin: 0; font-weight: 600;">Sus comentarios:</p>
      <p style="color: #7f1d1d; margin: 10px 0 0 0; font-style: italic;">"{{service_comments}}"</p>
    </div>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Nuestro equipo de atención al cliente se pondrá en contacto con usted en las próximas 24 horas para resolver cualquier inconveniente y asegurar su satisfacción.
    </p>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 20px;">
      Si desea comunicarse directamente, puede responder a este correo o llamarnos al <strong>(555) 123-4567</strong>.
    </p>
    
    <p style="color: #374151; line-height: 1.6;">
      Gracias por darnos la oportunidad de mejorar.<br>
      <strong>Taller Automotriz</strong>
    </p>
  </div>
</div>`,
        status: 'active',
        isPreset: true,
        description: 'Automated follow-up for satisfaction ratings ≤3 stars',
        triggerCondition: 'satisfaction_rating <= 3',
        availableVariables: ['client_name', 'folio_ot', 'satisfaction_rating', 'service_comments']
      },
      {
        id: 'preset-critical-issue',
        templateName: 'Critical Service Issue Alert',
        segment: 'feedback_critical_issue',
        subjectLine: 'Atención Urgente Requerida - Folio {{folio_ot}}',
        templateBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb;">
  <div style="background-color: #ffffff; border-radius: 8px; padding: 30px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
    <div style="background-color: #fee2e2; border-radius: 8px; padding: 15px; margin-bottom: 20px; text-align: center;">
      <h2 style="color: #991b1b; margin: 0;">⚠️ PROBLEMA CRÍTICO DETECTADO</h2>
    </div>
    
    <h3 style="color: #374151; margin-bottom: 15px;">Estimado/a {{client_name}},</h3>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Hemos detectado un problema crítico reportado en su retroalimentación para el servicio del folio <strong style="color: #dc2626;">{{folio_ot}}</strong>.
    </p>
    
    <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 20px; margin: 20px 0; border-radius: 8px;">
      <p style="color: #991b1b; margin: 0 0 10px 0; font-weight: 700; font-size: 16px;">Detalles del problema:</p>
      <p style="color: #7f1d1d; margin: 0; font-size: 15px; line-height: 1.5;">{{service_comments}}</p>
    </div>
    
    <div style="background-color: #dbeafe; border-left: 4px solid #2563eb; padding: 15px; margin: 20px 0; border-radius: 4px;">
      <p style="color: #1e40af; margin: 0; font-weight: 600;">Acción Inmediata:</p>
      <p style="color: #1e3a8a; margin: 10px 0 0 0;">
        Un supervisor se comunicará con usted en las próximas <strong>2 horas</strong> para resolver este asunto de manera prioritaria.
      </p>
    </div>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Calificación recibida: <strong style="color: #dc2626;">{{satisfaction_rating}} estrellas</strong>
    </p>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 20px;">
      Para atención inmediata, contáctenos:<br>
      📞 <strong>(555) 123-4567</strong> (Línea directa de supervisión)<br>
      📧 Responda a este correo
    </p>
    
    <p style="color: #374151; line-height: 1.6;">
      Lamentamos profundamente los inconvenientes y estamos comprometidos a resolverlo de inmediato.<br><br>
      <strong>Equipo de Supervisión<br>Taller Automotriz</strong>
    </p>
  </div>
</div>`,
        status: 'active',
        isPreset: true,
        description: 'Urgent response for critical issues detected in feedback comments (problema, falla, queja, insatisfecho, deficiente)',
        triggerCondition: 'service_comments CONTAINS critical keywords',
        availableVariables: ['client_name', 'folio_ot', 'satisfaction_rating', 'service_comments']
      },
      {
        id: 'preset-satisfaction-followup',
        templateName: 'Positive Satisfaction Follow-up',
        segment: 'feedback_positive',
        subjectLine: '¡Gracias por su confianza! - Folio {{folio_ot}}',
        templateBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb;">
  <div style="background-color: #ffffff; border-radius: 8px; padding: 30px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
    <div style="background-color: #d1fae5; border-radius: 8px; padding: 15px; margin-bottom: 20px; text-align: center;">
      <h2 style="color: #065f46; margin: 0;">⭐ ¡GRACIAS POR SU CALIFICACIÓN!</h2>
    </div>
    
    <h3 style="color: #374151; margin-bottom: 15px;">Estimado/a {{client_name}},</h3>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Nos complace saber que quedó satisfecho/a con nuestro servicio para el folio <strong style="color: #059669;">{{folio_ot}}</strong>.
    </p>
    
    <div style="background-color: #ecfdf5; border-left: 4px solid #059669; padding: 20px; margin: 20px 0; border-radius: 4px;">
      <p style="color: #065f46; margin: 0 0 10px 0; font-weight: 600; font-size: 18px;">
        Su calificación: <span style="color: #f59e0b;">{{satisfaction_rating}} ⭐</span>
      </p>
      <p style="color: #047857; margin: 0; font-style: italic; line-height: 1.5;">
        "{{service_comments}}"
      </p>
    </div>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Su opinión positiva motiva a nuestro equipo a seguir brindando el mejor servicio automotriz. Comentarios como el suyo nos ayudan a mantener nuestros altos estándares de calidad.
    </p>
    
    <div style="background-color: #eff6ff; border: 2px dashed #3b82f6; padding: 20px; margin: 20px 0; border-radius: 8px; text-align: center;">
      <p style="color: #1e40af; margin: 0 0 10px 0; font-weight: 600; font-size: 16px;">🎁 Beneficio Especial</p>
      <p style="color: #1e3a8a; margin: 0; line-height: 1.5;">
        Como agradecimiento, reciba un <strong>10% de descuento</strong> en su próximo servicio de mantenimiento.<br>
        <span style="font-size: 14px; color: #6b7280;">(Válido por 30 días)</span>
      </p>
    </div>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 15px;">
      Si necesita cualquier servicio adicional o tiene alguna pregunta, no dude en contactarnos:
    </p>
    
    <p style="color: #374151; line-height: 1.6; margin-bottom: 20px;">
      📞 <strong>(555) 123-4567</strong><br>
      📧 <strong>contacto@tallerautomotriz.com</strong><br>
      🌐 <strong>www.tallerautomotriz.com</strong>
    </p>
    
    <p style="color: #374151; line-height: 1.6;">
      ¡Esperamos verle pronto!<br><br>
      <strong>Equipo de Atención al Cliente<br>Taller Automotriz</strong>
    </p>
  </div>
</div>`,
        status: 'active',
        isPreset: true,
        description: 'Thank you message for positive satisfaction ratings (4-5 stars)',
        triggerCondition: 'satisfaction_rating >= 4',
        availableVariables: ['client_name', 'folio_ot', 'satisfaction_rating', 'service_comments']
      }
    ];
  },

  async createFeedbackTemplate(presetId) {
    try {
      const presets = this.getFeedbackTemplatePresets();
      const preset = presets?.find(p => p?.id === presetId);
      
      if (!preset) {
        throw new Error('Preset template not found');
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const templateData = {
        template_name: preset?.templateName,
        segment: preset?.segment,
        subject_line: preset?.subjectLine,
        template_body: preset?.templateBody,
        status: preset?.status,
        created_by: user?.id,
        metadata: {
          is_preset: true,
          description: preset?.description,
          trigger_condition: preset?.triggerCondition,
          available_variables: preset?.availableVariables
        }
      };

      const { data, error } = await supabase
        ?.from('email_templates')
        ?.insert(templateData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating feedback template:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create feedback template:', error);
      throw error;
    }
  },

  async getTemplateById(templateId) {
    try {
      const { data, error } = await supabase
        ?.from('email_templates')
        ?.select(`
          *,
          creator:user_profiles!email_templates_created_by_fkey(
            id,
            full_name,
            email
          ),
          versions:template_versions(
            id,
            version_number,
            subject_line,
            template_body,
            created_at
          )
        `)
        ?.eq('id', templateId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching template:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch template:', error);
      throw error;
    }
  },

  async createTemplate(templateData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({
        ...templateData,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('email_templates')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating template:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create template:', error);
      throw error;
    }
  },

  async updateTemplate(templateId, templateData) {
    try {
      const snakeData = toSnakeCase(templateData);

      const { data, error } = await supabase
        ?.from('email_templates')
        ?.update(snakeData)
        ?.eq('id', templateId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating template:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update template:', error);
      throw error;
    }
  },

  async deleteTemplate(templateId) {
    try {
      const { error } = await supabase
        ?.from('email_templates')
        ?.delete()
        ?.eq('id', templateId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error deleting template:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw error;
    }
  },

  async duplicateTemplate(templateId) {
    try {
      const template = await this.getTemplateById(templateId);
      if (!template) throw new Error('Template not found');

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const newTemplate = {
        templateName: `${template?.templateName} (Copy)`,
        subjectLine: template?.subjectLine,
        templateBody: template?.templateBody,
        segment: template?.segment,
        status: 'draft',
        createdBy: user?.id
      };

      return await this.createTemplate(newTemplate);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      throw error;
    }
  },

  replaceVariables(template, clientData) {
    let result = template;
    const variables = {
      client_name: clientData?.name || 'Valued Client',
      service_count: clientData?.serviceCount || 0,
      total_revenue: clientData?.totalRevenue?.toFixed(2) || '0.00',
      avg_invoice: clientData?.avgInvoice?.toFixed(2) || '0.00',
      client_lifetime_value: clientData?.clientLifetimeValue?.toFixed(2) || '0.00',
      first_service_date: clientData?.firstServiceDate ? new Date(clientData?.firstServiceDate)?.toLocaleDateString('es-MX') : 'N/A',
      last_service_date: clientData?.lastServiceDate ? new Date(clientData?.lastServiceDate)?.toLocaleDateString('es-MX') : 'N/A',
      month: new Date()?.toLocaleDateString('es-MX', { month: 'long' }),
      year: new Date()?.getFullYear()
    };

    Object.keys(variables)?.forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result?.replace(regex, variables?.[key]);
    });

    return result;
  },

  async getSegmentedClients(segment, filters = {}) {
    try {
      const clientData = await clientAnalyticsService?.getClientRevenueData(filters);

      if (segment === 'high_value') {
        return clientData
          ?.sort((a, b) => b?.clientLifetimeValue - a?.clientLifetimeValue)
          ?.slice(0, 20);
      }

      if (segment === 'repeat') {
        return clientData?.filter(client => client?.serviceCount >= 3);
      }

      if (segment === 'at_risk') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
        return clientData?.filter(client => {
          const lastService = new Date(client?.lastServiceDate);
          return lastService < thirtyDaysAgo && client?.serviceCount >= 2;
        });
      }

      return clientData;
    } catch (error) {
      console.error('Failed to get segmented clients:', error);
      throw error;
    }
  },

  async getSendHistory(templateId) {
    try {
      const { data, error } = await supabase
        ?.from('template_sends')
        ?.select('*')
        ?.eq('template_id', templateId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching send history:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch send history:', error);
      throw error;
    }
  },

  async recordSend(templateId, sendData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({
        templateId,
        ...sendData,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('template_sends')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error recording send:', error?.message);
        return null;
      }

      await supabase
        ?.from('email_templates')
        ?.update({ 
          usage_count: supabase?.raw('usage_count + 1'),
          last_used_at: new Date()?.toISOString()
        })
        ?.eq('id', templateId);

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to record send:', error);
      throw error;
    }
  }
};